// sdk-guide-page.js — Guide SDK (refonte T5).
// CORRECTION D'ENTORSE : le contenu, auparavant 100% EN DUR, est désormais EXTERNALISÉ dans
// src/data/sdk/*.json (overview, interaction-model, technical-properties, examples, deployment) et
// chargé via require.context. Migrée sur le design system (dark mode).
import React, { useMemo, useState } from 'react';
import { Book, MessageSquare, Layers, Coffee, Upload, CheckCircle, Wifi, ArrowRight } from 'lucide-react';
import { Card, Badge, Accordion, CodeBlock, Icon, EmptyState } from '../components/ui';
import { agentOf } from '../theme/tokens';

const sdkContext = require.context('../data/sdk', false, /\.json$/);
const codeOf = (c) => (Array.isArray(c) ? c.join('\n') : c || '');

const STATUS_COLOR = { PENDING: '#F59E0B', IN_PROGRESS: '#3B82F6', COMPLETED: '#10B981', FAILED: '#EF4444', BLOCKED: '#6B7280', SKIPPED: '#6B7280' };

const SDKGuidePage = () => {
  // Chargement des données SDK -> map { 'overview': {...}, 'interaction-model': {...}, ... }
  const data = useMemo(() => {
    const map = {};
    sdkContext.keys().forEach((k) => { map[k.replace('./', '').replace('.json', '')] = sdkContext(k); });
    return map;
  }, []);

  const overview = data.overview;
  const model = data['interaction-model'];
  const tech = data['technical-properties'];
  const examples = data.examples;
  const deployment = data.deployment;

  const sections = [
    { id: 'overview', title: "Vue d'ensemble", icon: Book, available: !!overview },
    { id: 'interaction', title: 'Modèle Interaction', icon: MessageSquare, available: !!model },
    { id: 'technical', title: 'Propriétés techniques', icon: Layers, available: !!tech },
    { id: 'examples', title: 'Exemples', icon: Coffee, available: !!examples },
    { id: 'deployment', title: 'Déploiement', icon: Upload, available: !!deployment },
  ].filter((s) => s.available);

  const [active, setActive] = useState(sections[0]?.id || 'overview');

  if (sections.length === 0) {
    return <EmptyState icon={Book} title="Guide SDK indisponible" message="Ajoutez des fichiers dans src/data/sdk/." />;
  }

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Navigation des sections */}
      <div className="lg:w-64 shrink-0">
        <div className="lg:sticky lg:top-4 space-y-1">
          {sections.map((s) => {
            const SIcon = s.icon;
            const on = active === s.id;
            return (
              <button key={s.id} onClick={() => setActive(s.id)}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg text-left transition-colors ${on ? 'bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'}`}>
                <SIcon className="h-4 w-4 shrink-0" />{s.title}
              </button>
            );
          })}
        </div>
      </div>

      {/* Contenu */}
      <div className="flex-1 min-w-0">
        {active === 'overview' && overview && (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{overview.metadata?.title || "Vue d'ensemble"}</h1>
              <p className="mt-2 text-gray-600 dark:text-gray-300">{overview.intro}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(overview.cycles || []).map((c) => (
                <Card key={c.id} className="p-5" style={{ borderTop: `3px solid ${c.color}` }}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{c.title}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{c.description}</p>
                  <ul className="mt-3 space-y-1">
                    {(c.points || []).map((p, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300"><CheckCircle className="h-4 w-4 mt-0.5 shrink-0" style={{ color: c.color }} />{p}</li>)}
                  </ul>
                </Card>
              ))}
            </div>
            {overview.websocket && (
              <Card className="p-5">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2"><Wifi className="h-5 w-5 text-blue-500" />{overview.websocket.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{overview.websocket.description}</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                  {(overview.websocket.flows || []).map((f, i) => (
                    <div key={i} className="text-center p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                      <ArrowRight className="h-5 w-5 text-blue-500 mx-auto mb-1" />
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{f.label}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{f.sub}</p>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}

        {active === 'interaction' && model && (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{model.metadata?.title || 'Modèle Interaction'}</h1>
              <p className="mt-2 text-gray-600 dark:text-gray-300">{model.metadata?.description}</p>
            </div>
            <Accordion title="Structure de l'interaction" defaultOpen>
              <CodeBlock code={model.example} language="json" />
              <div className="mt-4 space-y-1">
                {(model.fields || []).map((f) => (
                  <div key={f.name} className="text-sm"><code className="text-blue-600 dark:text-blue-400 font-mono">{f.name}</code> <span className="text-gray-400">: {f.type}</span> — <span className="text-gray-600 dark:text-gray-300">{f.description}</span></div>
                ))}
              </div>
            </Accordion>
            <Accordion title="Types d'interactions">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(model.types || {}).map(([group, items]) => (
                  <div key={group}>
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">{group}</h4>
                    <div className="space-y-2">
                      {items.map((it) => (
                        <div key={it.type} className="p-2 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                          <code className="text-xs font-mono font-semibold text-gray-800 dark:text-gray-100">{it.type}</code>
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{it.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Accordion>
            <Accordion title="États des interactions">
              <div className="flex flex-wrap gap-3">
                {(model.statuses || []).map((s) => (
                  <div key={s.status} className="flex items-center gap-2">
                    <Badge color={STATUS_COLOR[s.status]}>{s.status}</Badge>
                    <span className="text-sm text-gray-600 dark:text-gray-300">{s.description}</span>
                  </div>
                ))}
              </div>
            </Accordion>
          </div>
        )}

        {active === 'technical' && tech && (
          <div className="space-y-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{tech.metadata?.title || 'Propriétés techniques'}</h1>
              <p className="mt-2 text-gray-600 dark:text-gray-300">{tech.intro}</p>
            </div>
            {(tech.agents || []).map((a) => {
              const info = agentOf(a.agentType);
              return (
                <Accordion key={a.agentType} title={
                  <span className="flex items-center gap-2">{info && <Icon name={info.icon} className="h-4 w-4" style={{ color: info.color }} />}{a.label}</span>
                }>
                  <div className="space-y-1">
                    {(a.properties || []).map((p, i) => (
                      <div key={i} className="text-sm"><code className="text-blue-600 dark:text-blue-400 font-mono">{p.path}</code> — <span className="text-gray-600 dark:text-gray-300">{p.desc}</span></div>
                    ))}
                  </div>
                </Accordion>
              );
            })}
          </div>
        )}

        {active === 'examples' && examples && (
          <div className="space-y-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{examples.metadata?.title || 'Exemples'}</h1>
              <p className="mt-2 text-gray-600 dark:text-gray-300">{examples.metadata?.description}</p>
            </div>
            {(examples.examples || []).map((ex, i) => (
              <Accordion key={ex.id} title={ex.title} defaultOpen={i === 0}>
                <CodeBlock code={codeOf(ex.code)} language={ex.language} />
              </Accordion>
            ))}
          </div>
        )}

        {active === 'deployment' && deployment && (
          <div className="space-y-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{deployment.metadata?.title || 'Déploiement'}</h1>
              <p className="mt-2 text-gray-600 dark:text-gray-300">{deployment.metadata?.description}</p>
            </div>
            {(deployment.items || []).map((it, i) => (
              <Accordion key={it.id} title={it.title} defaultOpen={i === 0}>
                <CodeBlock code={codeOf(it.code)} language={it.language} />
              </Accordion>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SDKGuidePage;
